import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { FullScreenDialog } from '@/components/ui/full-screen-dialog';
import { Input } from '@/components/ui/input';
import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import { useStrikeTeams } from 'components/IAP/hooks/strikeTeamHooks';
import { type OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  useCreateOrgEvent,
  useUpdateOrgEvent,
  type OrgEvent,
} from 'hooks/orgEvents/orgEventHooks';
import { useAors } from 'hooks/useAors';
import { useAorText } from 'utils/aorTerminology';
import { useRosters } from 'hooks/useRosters';
import {
  selectGroupGuid,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import NotificationComposeFields, {
  type NotificationComposeHandle,
} from '../NotificationsTab/NotificationComposeFields';
import { CategoryFields, SeverityToggle } from './eventCategoryFields';
import { ChecklistConfigEditor } from './ChecklistConfigEditor';
import { CreateEventAssetChecklists } from './CreateEventAssetChecklists';
import {
  CREATE_EVENT_FORM_DEFAULTS,
  createEventAssetStep,
  createEventChecklistStep,
  createEventDetailsStep,
  createEventFormSchema,
  eventNotifyConfigFromNotification,
  eventToFormValues,
  formToCreateEventPayload,
  formToUpdateEventPayload,
  latLonFromGeometry,
  type CreateEventFormValues,
  type CreateEventStepId,
} from './createEventFormModel';

const IncidentGeoLocationPicker = lazy(() =>
  import('components/IncidentConfig/IncidentGeoLocationPicker').then(
    (module) => ({ default: module.IncidentGeoLocationPicker })
  )
);
import { registerEventsTourApi } from '../../eventsTour/eventsTourBridge';
import { RuleStepNav } from './RuleStep';
import { type RuleStepState } from './ruleStepModel';

const EVENT_STEP_NAV: {
  id: CreateEventStepId;
  index: number;
  title: string;
}[] = [
  { id: 'event', index: 1, title: 'Event details' },
  { id: 'notify', index: 2, title: 'Notify people' },
  { id: 'checklist', index: 3, title: 'Response Plan' },
  { id: 'assetChecklists', index: 4, title: 'Per-asset response plans' },
];

/** Local `YYYY-MM-DDTHH:mm` split into a calendar day and a 24-hour clock. */
function startedAtParts(value: string): { date?: Date; time: string } {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return { time: '' };
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = match[4];
  const minute = match[5];
  return {
    date: new Date(year, month - 1, day, Number(hour), Number(minute)),
    time: `${hour}:${minute}`,
  };
}

function toStartedAtValue(date: Date, time: string): string {
  const [hour = '00', minute = '00'] = time.split(':');
  const next = new Date(date);
  next.setHours(Number(hour) || 0, Number(minute) || 0, 0, 0);
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}T${pad(next.getHours())}:${pad(next.getMinutes())}`;
}

function StartedAtControl({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  const parts = startedAtParts(value);
  const [pendingTime, setPendingTime] = useState('');
  const time = parts.time || pendingTime;

  return (
    <div className="tw:flex tw:w-fit tw:flex-wrap tw:items-center tw:gap-2">
      <DatePicker
        id="create-event-started"
        value={parts.date}
        aria-label="Started at date"
        onChange={(next) => {
          if (!next) {
            onChange('');
            return;
          }
          onChange(toStartedAtValue(next, time || '00:00'));
          setPendingTime('');
        }}
      />
      <TimePicker
        value={time}
        aria-label="Started at time"
        onChange={(next) => {
          if (!parts.date) {
            setPendingTime(next);
            return;
          }
          onChange(toStartedAtValue(parts.date, next || '00:00'));
        }}
      />
    </div>
  );
}

type CreateOrgEventDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event?: OrgEvent;
};

export const CreateOrgEventDialog: FC<CreateOrgEventDialogProps> = ({
  open,
  onOpenChange,
  event,
}) => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const userGuid = useAppSelector(selectUserGuid);
  const { data: rosters } = useRosters(groupGuid ? [groupGuid] : []);
  const { data: aors = [] } = useAors();
  const aorText = useAorText();
  const { data: strikeTeams = [] } = useStrikeTeams();
  const { data: resources = [] } = useResourceSets();
  const createEvent = useCreateOrgEvent();
  const updateEvent = useUpdateOrgEvent();
  const isEdit = event != null;
  const [openStep, setOpenStep] = useState<CreateEventStepId>('event');
  const [confirmCreate, setConfirmCreate] = useState(false);
  const notifyRef = useRef<NotificationComposeHandle>(null);
  const [notifyActivity, setNotifyActivity] = useState({
    summary: 'Not configured',
    ready: false,
  });
  const [notifyError, setNotifyError] = useState<string | null>(null);

  const form = useForm<CreateEventFormValues>({
    defaultValues: CREATE_EVENT_FORM_DEFAULTS,
  });

  const aorIdByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const aor of aors) {
      map.set(aor.name, String(aor.id));
      map.set(aor.name.toLowerCase(), String(aor.id));
    }
    return map;
  }, [aors]);

  useEffect(() => {
    if (!open) {
      form.reset(CREATE_EVENT_FORM_DEFAULTS);
      setOpenStep('event');
      return;
    }
    form.reset(
      event ? eventToFormValues(event, aorIdByName) : CREATE_EVENT_FORM_DEFAULTS
    );
    setOpenStep('event');
  }, [open, event, aorIdByName, form]);

  useEffect(() => {
    return registerEventsTourApi({
      setCreateStep: (id) => setOpenStep(id),
    });
  }, []);

  const watched = useWatch({ control: form.control });
  const values = (watched ??
    CREATE_EVENT_FORM_DEFAULTS) as CreateEventFormValues;
  const checklistItems = form.watch('checklistItems') ?? [];

  const aorNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const aor of aors) map.set(String(aor.id), aor.name);
    return map;
  }, [aors]);

  const aorOptions = useMemo(
    () => aors.map((aor) => ({ value: String(aor.id), label: aor.name })),
    [aors]
  );

  const userOptions = useMemo(
    () =>
      (rosters ?? []).map((user) => ({
        value: user.user_guid,
        label: user.email_address,
      })),
    [rosters]
  );

  const teamOptions = useMemo(
    () =>
      strikeTeams.map((team) => ({
        value: team.strike_team_id || team.id,
        label: team.name,
      })),
    [strikeTeams]
  );

  const resourceOptions = useMemo(
    () =>
      resources
        .map((resource) => ({
          value: resource.resource_set_id,
          label: resource.name || resource.resource_set_id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [resources]
  );

  const resourceNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const resource of resources) {
      map.set(
        resource.resource_set_id,
        resource.name || resource.resource_set_id
      );
    }
    return map;
  }, [resources]);

  const steps: Record<CreateEventStepId, RuleStepState> = {
    event: createEventDetailsStep(values),
    notify: {
      status: notifyError
        ? 'incomplete'
        : notifyActivity.ready
          ? 'complete'
          : 'incomplete',
      summary: notifyActivity.summary,
      errors: notifyError ? [notifyError] : [],
      warnings: [],
    },
    checklist: createEventChecklistStep(values, { additive: isEdit }),
    assetChecklists: createEventAssetStep(values),
  };

  const setChecklistItems = (next: OrgEventChecklistItemDef[]) => {
    form.setValue('checklistItems', next, { shouldDirty: true });
  };

  const onSubmit = form.handleSubmit(async (formValues) => {
    if (!groupGuid) return;
    const parsed = createEventFormSchema.safeParse(formValues);
    if (!parsed.success) {
      const nameError = parsed.error.issues.find(
        (issue) => issue.path[0] === 'name'
      )?.message;
      if (nameError) {
        form.setError('name', { type: 'manual', message: nameError });
      }
      setOpenStep('event');
      return;
    }
    const collected = notifyRef.current?.collect({ allowEmpty: true });
    if (collected?.status === 'error') {
      setNotifyError(collected.message);
      setOpenStep('notify');
      return;
    }
    setNotifyError(null);
    const notify =
      collected?.status === 'ready'
        ? eventNotifyConfigFromNotification(collected.fields)
        : undefined;
    try {
      if (isEdit && event) {
        const payload = formToUpdateEventPayload(parsed.data, {
          aorNameById,
          resourceNameById,
        });
        await updateEvent.mutateAsync({ id: event.id, ...payload, notify });
        toast.success('Event updated');
      } else {
        const payload = formToCreateEventPayload(parsed.data, {
          groupGuid,
          userGuid: userGuid ?? null,
          aorNameById,
          resourceNameById,
        });
        await createEvent.mutateAsync({ ...payload, notify });
        toast.success('Event created');
      }
      onOpenChange(false);
    } catch {
      toast.error(
        isEdit
          ? 'Could not update the event. Try again.'
          : 'Could not create the event. Try again.'
      );
    }
  });

  const stepIndex = EVENT_STEP_NAV.findIndex((step) => step.id === openStep);
  const nextStep = EVENT_STEP_NAV[stepIndex + 1];
  const unfinishedSteps = EVENT_STEP_NAV.filter(
    (step) => steps[step.id].status !== 'complete'
  );

  const requestCreate = () => {
    if (isEdit) {
      void onSubmit();
      return;
    }
    if (!form.getValues('name').trim()) {
      form.setError('name', { type: 'manual', message: 'Name is required' });
      setOpenStep('event');
      return;
    }
    if (unfinishedSteps.length > 0) {
      setConfirmCreate(true);
      return;
    }
    void onSubmit();
  };

  return (
    <>
      <FullScreenDialog
        open={open}
        onOpenChange={onOpenChange}
        title={isEdit ? 'Edit Event' : 'Create Event'}
        description={
          isEdit
            ? undefined
            : 'Set the event fields now. Notifications and the Response Plan apply as soon as you create it — not when a rule fires later.'
        }
        formProps={{
          onSubmit: (event) => {
            const submitter = (event.nativeEvent as SubmitEvent).submitter;
            if (
              !(submitter instanceof HTMLElement) ||
              submitter.dataset.eventSubmit !== 'true'
            ) {
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            void onSubmit(event);
          },
        }}
        railClassName="tw:!flex tw:!w-80"
        rail={
          <nav
            aria-label="Event steps"
            className="tw:flex tw:flex-col tw:gap-1"
          >
            {EVENT_STEP_NAV.map((step) => (
              <RuleStepNav
                key={step.id}
                index={step.index}
                title={step.title}
                summary={steps[step.id].summary}
                status={steps[step.id].status}
                selected={openStep === step.id}
                onSelect={() => setOpenStep(step.id)}
              />
            ))}
          </nav>
        }
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            {nextStep ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpenStep(nextStep.id)}
              >
                Next
              </Button>
            ) : null}
            <Button
              type="button"
              disabled={
                createEvent.isPending || updateEvent.isPending || !groupGuid
              }
              data-tour="create-event-submit"
              onClick={requestCreate}
            >
              {isEdit
                ? updateEvent.isPending
                  ? 'Saving…'
                  : 'Save'
                : createEvent.isPending
                  ? 'Creating…'
                  : 'Create'}
            </Button>
          </>
        }
      >
        <div className="tw:min-w-0">
          {openStep === 'event' ? (
            <div data-tour="create-event-details" className="tw:space-y-3">
              <Field>
                <FieldLabel htmlFor="create-event-name">
                  <span>
                    Event name
                    <span aria-hidden="true"> *</span>
                  </span>
                </FieldLabel>
                <Input
                  id="create-event-name"
                  aria-required="true"
                  {...form.register('name')}
                />
                {form.formState.errors.name ? (
                  <p className="tw:m-0 tw:text-xs tw:text-destructive">
                    {form.formState.errors.name.message}
                  </p>
                ) : null}
              </Field>
              <Controller
                control={form.control}
                name="type"
                render={({ field: presetField }) => (
                  <Controller
                    control={form.control}
                    name="customCategory"
                    render={({ field: customField }) => (
                      <CategoryFields
                        preset={presetField.value}
                        custom={customField.value ?? ''}
                        onPresetChange={presetField.onChange}
                        onCustomChange={customField.onChange}
                        customInputId="create-event-custom-category"
                      />
                    )}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="severity"
                render={({ field }) => (
                  <SeverityToggle
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <Field>
                <div className="tw:flex tw:items-center tw:gap-1">
                  <FieldLabel>{aorText('Responsible AOR')}</FieldLabel>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <span
                            role="button"
                            tabIndex={0}
                            aria-label="About Responsible AOR"
                            className="tw:inline-flex tw:shrink-0 tw:text-muted-foreground"
                            onClick={(event) => event.preventDefault()}
                            onPointerDown={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                          >
                            <Info className="tw:h-3.5 tw:w-3.5" />
                          </span>
                        }
                      />
                      <TooltipContent side="bottom" className="tw:max-w-xs">
                        This is the AOR or Business Unit responsible for or
                        taking lead on the Event.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Controller
                  control={form.control}
                  name="aorIds"
                  render={({ field }) => (
                    <MultiIdCombobox
                      options={aorOptions}
                      value={field.value ?? []}
                      onChange={field.onChange}
                      placeholder={aorText('Search AORs…')}
                    />
                  )}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="create-event-description">
                  Event description
                </FieldLabel>
                <Textarea
                  id="create-event-description"
                  rows={2}
                  {...form.register('thresholdDescription')}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="create-event-started">
                  Started at
                </FieldLabel>
                <Controller
                  control={form.control}
                  name="startedAt"
                  render={({ field }) => (
                    <StartedAtControl
                      value={field.value ?? ''}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Field>
              <Field>
                <FieldLabel>Location</FieldLabel>
                <FieldDescription>
                  Optional. Search for a place, drop a point, or draw the
                  affected area.
                </FieldDescription>
                <Suspense
                  fallback={
                    <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                      Loading the map…
                    </p>
                  }
                >
                  <IncidentGeoLocationPicker
                    geometry={values.geometry ?? undefined}
                    onGeometryChange={(next) => {
                      const { lat, lon } = latLonFromGeometry(next);
                      form.setValue('geometry', next, { shouldDirty: true });
                      form.setValue('lat', lat, { shouldDirty: true });
                      form.setValue('lon', lon, { shouldDirty: true });
                    }}
                    onAddressChange={() => {}}
                  />
                </Suspense>
              </Field>
              <StepMessages
                errors={steps.event.errors}
                warnings={steps.event.warnings}
              />
            </div>
          ) : null}

          <div
            data-tour="create-event-notify"
            className={openStep === 'notify' ? 'tw:space-y-3' : 'tw:hidden'}
          >
            <NotificationComposeFields
              ref={notifyRef}
              rolesTitle="Roles & Scopes"
              allowSchedule={false}
              onActivity={setNotifyActivity}
            />
            <FieldDescription>
              {isEdit
                ? 'Sent when you save if this notification has recipients. Existing notifications are not changed.'
                : 'Sent when you create this event, if this notification has recipients.'}
            </FieldDescription>
            <StepMessages errors={steps.notify.errors} warnings={[]} />
          </div>

          {openStep === 'checklist' ? (
            <div data-tour="create-event-checklist" className="tw:space-y-3">
              <FieldDescription>
                {isEdit
                  ? 'New tasks are added to the Response Plan when you save. Existing tasks stay editable on the event card.'
                  : 'Tasks become org tasks when you create this event. Click an item to edit status, point of contact and sub-tasks, or channels and messages for Send Notification.'}
              </FieldDescription>
              <ChecklistConfigEditor
                items={checklistItems}
                onChange={setChecklistItems}
                userOptions={userOptions}
                teamOptions={teamOptions}
                addLabel="Add Task"
                allowTemplateVariables={false}
                assignPersonOrRole
                defaultAorIds={values.aorIds ?? []}
              />
            </div>
          ) : null}

          {openStep === 'assetChecklists' ? (
            <div data-tour="create-event-assets" className="tw:space-y-3">
              <CreateEventAssetChecklists
                resourceOptions={resourceOptions}
                resources={resources}
                selectedIds={values.impactedResourceIds ?? []}
                onSelectedIdsChange={(next) =>
                  form.setValue('impactedResourceIds', next, {
                    shouldDirty: true,
                  })
                }
                checklistsByResourceId={values.perAssetChecklists ?? {}}
                onChecklistsChange={(next) =>
                  form.setValue('perAssetChecklists', next, {
                    shouldDirty: true,
                  })
                }
                userOptions={userOptions}
                teamOptions={teamOptions}
                assignPersonOrRole
                defaultAorIds={values.aorIds ?? []}
              />
              <StepMessages
                errors={steps.assetChecklists.errors}
                warnings={steps.assetChecklists.warnings}
              />
            </div>
          ) : null}
        </div>
        <AlertDialog open={confirmCreate} onOpenChange={setConfirmCreate}>
          <AlertDialogContent
            className="dark tw:!z-[80]"
            overlayClassName="tw:!z-[80]"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Create this event now?</AlertDialogTitle>
              <AlertDialogDescription>
                {unfinishedSteps.map((step) => step.title).join(', ')}{' '}
                {unfinishedSteps.length === 1 ? 'is' : 'are'} still open.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel type="button">Go back</AlertDialogCancel>
              <AlertDialogAction
                type="button"
                disabled={createEvent.isPending || !groupGuid}
                onClick={() => {
                  setConfirmCreate(false);
                  void onSubmit();
                }}
              >
                Create anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </FullScreenDialog>
    </>
  );
};

function StepMessages({
  errors,
  warnings,
}: {
  errors: string[];
  warnings: string[];
}) {
  const aorText = useAorText();
  if (errors.length === 0 && warnings.length === 0) return null;
  return (
    <div className="tw:space-y-1">
      {errors.map((message) => (
        <p key={message} className="tw:m-0 tw:text-xs tw:text-destructive">
          {aorText(message)}
        </p>
      ))}
      {warnings.map((message) => (
        <p key={message} className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          {aorText(message)}
        </p>
      ))}
    </div>
  );
}
